import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getDateLocale, resolveDateLocale } from './dateLocale';
import { usePopoverPosition } from './usePopoverPosition';
import {
  anchorMonth,
  buildValue,
  displayText,
  EMPTY_PARTS,
  isComplete,
  isReal,
  parseCommit,
  parseTimeStep,
  parseValue,
  todayDate,
  type ValueParts,
} from './dateBoxValue';
import type { DateBoxProps } from '../types';

/** Everything DateBox's chrome + popover need. Returned by {@link useDateBox}. */
export type DateBoxController = ReturnType<typeof useDateBox>;

/**
 * All DateBox state, refs, effects and handlers. The component itself only wires the returned
 * controller into `FieldAffix` and the portalled `DateBoxPopover`.
 */
export function useDateBox({
  value,
  onChange,
  mode = 'single',
  withTime = false,
  min = null,
  max = null,
  dayDisabled,
  dayTint,
  dayNote,
  showMonthCol = true,
  showYearCol = true,
  nights = false,
  openStart: openStartProp = true,
  openEnd: openEndProp = true,
  finite = false,
  timeStep = '5 / 4',
  minuteExpand = true,
  locale: localeProp,
  ...chrome
}: DateBoxProps) {
  const { t, i18n } = useTranslation('dateTimeField');
  const inputId = useId();
  const locale = getDateLocale(localeProp ?? resolveDateLocale(i18n.language));
  // `finite` je zkratka pro uzavřený interval — vypne oba otevřené konce (skryje ∞ markery i
  // auto-otevření z holého data). Default jsou intervaly otevřené.
  const openStart = finite ? false : openStartProp;
  const openEnd = finite ? false : openEndProp;

  const initial = parseValue(value, mode, withTime);
  const [parts, setParts] = useState<ValueParts>(initial);
  const [viewYear, setViewYear] = useState(initial.viewYear);
  const [viewMonth, setViewMonth] = useState(initial.viewMonth);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [hoverIso, setHoverIso] = useState<string | null>(null);
  const [hoverDir, setHoverDir] = useState<'fwd' | 'bwd'>('fwd');
  const [infHover, setInfHover] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastValueRef = useRef(value);
  const infHoverRef = useRef(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popStyle = usePopoverPosition(open, anchorRef, popRef);

  infHoverRef.current = infHover;

  useEffect(() => {
    if (value !== lastValueRef.current) {
      const next = parseValue(value, mode, withTime);
      setParts({
        from: next.from,
        to: next.to,
        fH: next.fH,
        fM: next.fM,
        tH: next.tH,
        tM: next.tM,
      });
      setViewYear(next.viewYear);
      setViewMonth(next.viewMonth);
      setEditing(false);
      setInvalid(false);
      setHoverIso(null);
      lastValueRef.current = value;
    }
  }, [value, mode, withTime]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      setOpen(false);
      setEditing(false);
      setHoverIso(null);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  const isDisabled = (iso: string): boolean => {
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return dayDisabled?.(iso) ?? false;
  };

  const commitParts = (next: ValueParts) => {
    setParts(next);
    const canonical = buildValue(next, mode, withTime);
    if (isComplete(next, mode)) {
      if (canonical !== lastValueRef.current) {
        lastValueRef.current = canonical;
        onChange(canonical);
      }
    } else if (!next.from && !next.to && lastValueRef.current !== '') {
      lastValueRef.current = '';
      onChange('');
    }
  };

  const anchorView = (source: ValueParts) => {
    const { viewYear: y, viewMonth: m } = anchorMonth(source);
    setViewYear(y);
    setViewMonth(m);
  };

  const openWithView = () => {
    anchorView(parts);
    setOpen(true);
  };

  const selectDay = (iso: string) => {
    if (isDisabled(iso)) return;
    setEditing(false);
    setInvalid(false);
    setHoverIso(null);
    if (mode === 'single') {
      const next: ValueParts = { ...parts, from: iso, to: null };
      if (withTime && next.fH == null) {
        next.fH = 0;
        next.fM = 0;
      } else if (!withTime) {
        setOpen(false);
      }
      commitParts(next);
      return;
    }
    const fromReal = isReal(parts.from);
    if (!fromReal || parts.to) {
      // Start a new range — internal only, not yet emitted.
      const next: ValueParts = { ...parts, from: iso, to: null };
      if (withTime && next.fH == null) {
        next.fH = 0;
        next.fM = 0;
      }
      setParts(next);
      return;
    }
    let a = parts.from;
    let b = iso;
    if (iso < parts.from!) {
      a = iso;
      b = parts.from!;
    }
    const next: ValueParts = { ...parts, from: a, to: b };
    if (withTime) {
      if (next.tH == null) {
        next.tH = 0;
        next.tM = 0;
      }
    } else {
      setOpen(false);
    }
    commitParts(next);
  };

  const clickInfinity = (dir: 'fwd' | 'bwd') => {
    setInfHover(false);
    setHoverIso(null);
    if (dir === 'fwd') {
      const next: ValueParts = { ...parts, to: 'INF' };
      if (withTime && next.fH == null) {
        next.fH = 0;
        next.fM = 0;
      }
      if (!withTime) {
        setOpen(false);
        setEditing(false);
      }
      commitParts(next);
    } else {
      const next: ValueParts = {
        from: '-INF',
        to: parts.from,
        fH: null,
        fM: null,
        tH: withTime ? (parts.fH ?? 0) : parts.tH,
        tM: withTime ? (parts.fM ?? 0) : parts.tM,
      };
      if (!withTime) {
        setOpen(false);
        setEditing(false);
      }
      commitParts(next);
    }
  };

  const pickTime = (which: 'f' | 't', hour: number | null, minute: number | null) => {
    const next: ValueParts = { ...parts };
    if (which === 'f') {
      next.fH = hour != null ? hour : (parts.fH ?? 0);
      next.fM = minute != null ? minute : (parts.fM ?? 0);
    } else {
      next.tH = hour != null ? hour : (parts.tH ?? 0);
      next.tM = minute != null ? minute : (parts.tM ?? 0);
    }
    setEditing(false);
    setInvalid(false);
    commitParts(next);
  };

  const clearVal = () => {
    setEditing(false);
    setInvalid(false);
    commitParts(EMPTY_PARTS);
  };

  const goToday = () => {
    const today = todayDate();
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const closePopover = () => {
    setOpen(false);
    setEditing(false);
  };

  const commitText = () => {
    const result = parseCommit((text || '').trim(), {
      mode,
      withTime,
      locale,
      openStart,
      openEnd,
      min,
      max,
      isDisabled,
      current: parts,
    });
    switch (result.kind) {
      case 'clear':
        clearVal();
        return;
      case 'noop':
        setEditing(false);
        setInvalid(false);
        return;
      case 'invalid':
        setInvalid(true);
        setEditing(false);
        return;
      case 'set':
        setEditing(false);
        setInvalid(false);
        anchorView(result.parts);
        commitParts(result.parts);
        return;
    }
  };

  const cancelHoverClear = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };
  const clearHoverSoon = () => {
    if (mode !== 'range') return;
    cancelHoverClear();
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      if (!infHoverRef.current) setHoverIso(null);
    }, 200);
  };

  const placeholder = withTime
    ? mode === 'range'
      ? locale.placeholderRange
      : `${locale.placeholder} hh:mm`
    : mode === 'range'
      ? locale.placeholderRange
      : locale.placeholder;

  return {
    // config echoed to the popover
    chrome,
    inputId,
    locale,
    mode,
    withTime,
    nights,
    openStart,
    openEnd,
    showMonthCol,
    showYearCol,
    minuteExpand,
    timeStep: parseTimeStep(timeStep),
    dayTint,
    dayNote,
    hasDayNote: typeof dayNote === 'function',
    isDisabled,
    // refs + positioning
    anchorRef,
    inputRef,
    popRef,
    popStyle,
    // state
    parts,
    viewYear,
    viewMonth,
    open,
    hoverIso,
    hoverDir,
    infHover,
    hasValue: !!parts.from,
    // affix-derived
    inputText: editing ? text : displayText(parts, mode, withTime, locale),
    placeholder,
    invalid,
    errorText: invalid ? (mode === 'range' ? t('invalidRange') : t('invalidDate')) : chrome.error,
    openCalendarLabel: t('openCalendar'),
    // popover handlers
    selectDay,
    clickInfinity,
    pickTime,
    clearVal,
    goToday,
    setHoverIso,
    setHoverDir,
    setInfHover,
    cancelHoverClear,
    clearHoverSoon,
    setView: (y: number, m: number) => {
      setViewYear(y);
      setViewMonth(m);
    },
    closePopover,
    // affix handlers
    onAffixMouseDown: (event: MouseEvent<HTMLDivElement>) => {
      const el = event.target as HTMLElement;
      if (el.tagName === 'INPUT') return;
      if (el.closest('button')) return;
      event.preventDefault();
      inputRef.current?.focus();
      openWithView();
    },
    onInputChange: (next: string) => {
      setText(next);
      setEditing(true);
      setInvalid(false);
    },
    onInputFocus: () => {
      setEditing(true);
      setText(displayText(parts, mode, withTime, locale));
      openWithView();
    },
    onInputBlur: () => {
      commitText();
      chrome.onBlur?.();
    },
    onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitText();
        setOpen(false);
      } else if (event.key === 'Escape') {
        // When nested inside another popover, only close our own layer on the first Escape.
        if (open) event.stopPropagation();
        setOpen(false);
        setEditing(false);
      } else if (event.key === 'ArrowDown' && !open) {
        openWithView();
      }
    },
    onToggle: (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (open) setOpen(false);
      else openWithView();
    },
  };
}
